import {
  ColumnDirective,
  ColumnsDirective,
  EditSettingsModel,
  GridComponent,
  Inject,
  Page,
  Sort,
  Toolbar,
} from '@syncfusion/ej2-react-grids';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import '../../../../../core/styles/sortable-columns.css';

import { CustomSelectOption } from '@components/Form/CustomSelect';
import { DynamicRecordFilter, TableActions } from '@components/Grid';
import { Loading } from '@components/Loading';
import { PageContainer } from '@components/PageContainer';
import { routes } from '@config/routes';
import { useGridFilters } from '@hooks/useGridFilters';
import { useNotification } from '@hooks/useNotification';
import { useSyncfusionGrid } from '@hooks/useSyncfusionGrid';
import {
  RecurringInvoiceStatus,
  recurringInvoiceStatusSelectOptions,
} from '@models/RecurringInvoice';
import { RecurringInvoiceService } from '@services/RecurringInvoice';

export const RecurringInvoiceList: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const [customerFilterSelectOptions, setCustomerFilterSelectOptions] =
    useState<CustomSelectOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCustomerFilterLoading, setIsCustomerFilterLoading] =
    useState<boolean>(true);
  const [queryFilter, setQueryFilter] = useState<string>('');
  const navigate = useNavigate();

  // Create a service wrapper that matches useSyncfusionGrid interface
  const recurringInvoiceServiceWrapper = {
    ...RecurringInvoiceService,
    getList: async (
      page: number,
      take: number | undefined,
      sortBy: string | undefined,
      sortDirection: string | undefined,
      filters: string
    ) => {
      return RecurringInvoiceService.getList(
        page,
        take,
        undefined,
        undefined,
        filters,
        sortBy,
        sortDirection
      );
    },
  };

  // Custom hooks for grid functionality
  const { filters, updateFilterAndApply, clearFilters, applyFilters } =
    useGridFilters(
      {
        customerFilter: '',
        statusFilter: '',
        dateFromFilter: '',
        dateToFilter: '',
        invoiceNumberFilter: '',
      },
      setQueryFilter
    );

  const {
    gridData,
    gridRef,
    gridKey,
    renderComplete,
    dataStateChange,
    fetchData,
  } = useSyncfusionGrid({
    service: recurringInvoiceServiceWrapper,
    queryFilter,
    defaultSortBy: 'ID',
    defaultSortDirection: 'desc',
  });

  const editOptions: EditSettingsModel = {
    allowAdding: true,
    allowDeleting: true,
    allowEditing: true,
    mode: 'Dialog',
  };

  const loadCustomerFilterSelectOptions = () => {
    RecurringInvoiceService.getCustomerFilterSelectOptions().then((result) => {
      if (result.isSuccess && 'data' in result) {
        const customerSelectOptions = result.data.map((row) => ({
          value: row.value,
          text: row.text,
        }));
        setCustomerFilterSelectOptions(customerSelectOptions);

        setIsCustomerFilterLoading(false);
      }
    });
  };

  useEffect(() => {
    loadCustomerFilterSelectOptions();
  }, []);

  useEffect(() => {
    if (!isCustomerFilterLoading) {
      setIsLoading(false);
    }
  }, [isCustomerFilterLoading]);

  const handleEdit = (rowId: number) => {
    navigate(`${routes.RNC_RECURRING_INVOICES}/${rowId}/edit`);
  };

  const handleEndRecord = (id: number) => {
    RecurringInvoiceService.end(id).then((result) => {
      if (result.isSuccess) {
        fetchData(true);
        const successMessage = `Successfully ended the recurring invoice record.`;
        console.log(successMessage);
        showSuccess(successMessage);
      } else {
        const errorMessage = `Failed to end the recurring invoice record. ${result.errorMessages}`;
        showError(errorMessage);
        console.error(errorMessage);
      }
    });
  };

  const handleDuplicate = (rowId: number) => {
    RecurringInvoiceService.duplicate(rowId).then((result) => {
      if (result.isSuccess) {
        fetchData(true);
        const successMessage = `Successfully duplicated the recurring invoice record.`;
        console.log(successMessage);
        showSuccess(successMessage);
      } else {
        const errorMessage = `Failed to duplicate the recurring invoice record. ${result.errorMessages}`;
        showError(errorMessage);
        console.error(errorMessage);
      }
    });
  };

  const actionButtons = (props: any) => {
    return (
      <TableActions
        inlineActions={[
          {
            title: 'Duplicate',
            onClick: () => handleDuplicate(props.id),
          },
        ]}
        dropdownActions={[
          {
            title: 'Edit',
            onClick: () => handleEdit(props.id),
            isHidden: props.status !== RecurringInvoiceStatus.Draft,
          },
          {
            showTopSeparator: true,
            title: 'Activate',
            onClick: () =>
              RecurringInvoiceService.activate(props.id).then((result) => {
                if (result.isSuccess) {
                  fetchData(true);
                  const successMessage = `Recurring invoice activated successfully.`;
                  console.log(successMessage);
                  showSuccess(successMessage);
                } else {
                  const errorMessage = `Failed to activate recurring invoice: ${result.errorMessages}`;
                  console.error(errorMessage);
                  showError(errorMessage);
                }
              }),
          },
          {
            title: 'Suspend',
            onClick: () =>
              RecurringInvoiceService.suspend(props.id).then((result) => {
                if (result.isSuccess) {
                  const successMessage = `Recurring invoice suspended successfully.`;
                  console.log(successMessage);
                  showSuccess(successMessage);
                  fetchData(true);
                } else {
                  const errorMessage = `Failed to suspend recurring invoice: ${result.errorMessages}`;
                  console.error(errorMessage);
                  showError(errorMessage);
                }
              }),
          },
          {
            title: 'End',
            onClick: () => handleEndRecord(props.id),
          },
        ]}
      />
    );
  };

  if (isLoading) {
    return <Loading />;
  } else {
    return (
      <>
        <DynamicRecordFilter
          label="Filters"
          filters={[
            {
              label: 'Select Customer',
              type: 'select',
              selection: customerFilterSelectOptions,
              value: filters.customerFilter,
              onChange: (e) => {
                updateFilterAndApply('customerFilter', e.target.value, () =>
                  fetchData(true)
                );
              },
            },
            {
              label: 'Select Status',
              type: 'select',
              selection: recurringInvoiceStatusSelectOptions,
              value: filters.statusFilter,
              onChange: (e) => {
                updateFilterAndApply('statusFilter', e.target.value, () =>
                  fetchData(true)
                );
              },
            },
          ]}
          onFilter={() => {
            applyFilters(() => fetchData(true));
          }}
          onClear={() => {
            clearFilters();
            applyFilters(() => fetchData(true));
          }}
        />
        <PageContainer>
          <GridComponent
            key={gridKey}
            dataBound={renderComplete}
            dataSource={gridData}
            ref={gridRef}
            allowPaging={true}
            allowSorting={true}
            pageSettings={{
              pageSize: 10,
              pageSizes: [5, 10, 20, 50, 100],
            }}
            editSettings={editOptions}
            dataStateChange={dataStateChange}
          >
            <ColumnsDirective>
              <ColumnDirective
                field="statusDisplayString"
                headerText="Status"
                customAttributes={{ class: 'sortable-column' }}
              />
              <ColumnDirective
                field="recurringInvoiceNumber"
                headerText="Recurring Invoice Number"
                customAttributes={{ class: 'sortable-column' }}
              />
              <ColumnDirective
                field="customerCompanyName"
                headerText="Customer"
                customAttributes={{ class: 'sortable-column' }}
              />
              <ColumnDirective
                field="schedule"
                headerText="Schedule"
                allowSorting={false}
                customAttributes={{ class: 'non-sortable-column' }}
              />
              <ColumnDirective
                field="total"
                headerText="Total"
                customAttributes={{ class: 'sortable-column' }}
                template={(props: any) => {
                  return (
                    <span>
                      {Number(props.total).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  );
                }}
              />
              <ColumnDirective
                headerText="Actions"
                width={140}
                allowSorting={false}
                customAttributes={{ class: 'non-sortable-column' }}
                template={actionButtons}
              />
            </ColumnsDirective>
            <Inject services={[Page, Sort, Toolbar]} />
          </GridComponent>
        </PageContainer>
      </>
    );
  }
};
